import {Component, OnInit} from '@angular/core';
import {PAGES_AND_PATHS} from '../../../constants/pages-and-paths';
import {InvitationsHttpService} from '../../../services/http-services/invitations/invitations-http.service';

type InvitationGender = 'boys' | 'girls';
type InvitationMediaType = 'image' | 'video';

interface InvitationMediaItem {
  id?: number;
  name: string;
  fullPath?: string;
  posterPath?: string;
  type: InvitationMediaType;
  gender: InvitationGender;
  age: number;
  position: number;
  file?: File;
}

@Component({
  selector: 'app-invitations',
  templateUrl: './invitations.component.html',
})

export class InvitationsComponent implements OnInit {
  activeGender: InvitationGender = 'boys';
  activeAge = 1;
  activeMediaType: InvitationMediaType = 'image';
  isMediaModalOpen = false;
  isMediaLoading = false;
  isMediaLoadingMore = false;
  currentPage = 1;
  lastPage = 1;
  activeInvitationId: number | null = null;
  modalMediaItems: InvitationMediaItem[] = [];
  previewVideoItem: InvitationMediaItem | null = null;

  readonly genderOptions: { label: string; value: InvitationGender }[] = [
    {label: 'Decak', value: 'boys'},
    {label: 'Devojcica', value: 'girls'}
  ];
  readonly ageOptions = [1, 2, 3, 4, 5, 6, 7];
  readonly mediaTabs: { label: string; value: InvitationMediaType }[] = [
    {label: 'Slike', value: 'image'},
    {label: 'Video', value: 'video'}
  ];
  mediaItems: InvitationMediaItem[] = [];
  protected readonly PAGES_AND_PATHS = PAGES_AND_PATHS;

  constructor(private invitationsHttpService: InvitationsHttpService) {
  }

  ngOnInit(): void {
    this.getFilteredInvitations();
  }

  get filteredMediaItems(): InvitationMediaItem[] {
    return this.mediaItems
      .filter(item => item.gender === this.activeGender && item.age === this.activeAge && item.type === this.activeMediaType)
      .sort((a, b) => a.position - b.position);
  }

  get visibleMediaItems(): InvitationMediaItem[] {
    return this.filteredMediaItems;
  }

  get hasMoreItems(): boolean {
    return this.currentPage < this.lastPage;
  }

  changeGender(gender: InvitationGender): void {
    this.activeGender = gender;
    this.reloadFilteredInvitations();
  }

  changeAge(age: number): void {
    this.activeAge = age;
    this.reloadFilteredInvitations();
  }

  changeMediaType(type: InvitationMediaType): void {
    this.activeMediaType = type;
    this.reloadFilteredInvitations();
  }

  openMediaModal(type: InvitationMediaType): void {
    this.activeMediaType = type;
    this.modalMediaItems = this.filteredMediaItems.map(item => ({...item}));
    this.isMediaModalOpen = true;
  }

  closeMediaModal(): void {
    this.isMediaModalOpen = false;
    this.modalMediaItems = [];
  }

  onConfirmMediaModal(_event?: any): void {
    this.closeMediaModal();
    this.reloadFilteredInvitations();
  }

  openVideoPreview(item: InvitationMediaItem): void {
    if (item.type !== 'video' || !item.fullPath) return;

    this.previewVideoItem = item;
  }

  closeVideoPreview(): void {
    this.previewVideoItem = null;
  }

  onMediaGridScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceFromBottom < 120 && this.hasMoreItems && !this.isMediaLoadingMore && !this.isMediaLoading) {
      this.getFilteredInvitations(true);
    }
  }

  formatAge(age: number): string {
    return age === 7 ? '7+ Rodjendan' : `${age}. Rodjendan`;
  }

  private reloadFilteredInvitations(): void {
    this.currentPage = 1;
    this.lastPage = 1;
    this.mediaItems = [];
    this.getFilteredInvitations();
  }

  private getFilteredInvitations(append = false): void {
    const page = append ? this.currentPage + 1 : 1;
    this.isMediaLoading = !append;
    this.isMediaLoadingMore = append;
    this.invitationsHttpService.getFilteredInvitations({
      gender: this.activeGender,
      age: this.activeAge,
      type: this.getApiMediaType(),
      page
    }).then(res => {
      const nextItems = this.mapFilteredInvitationsResponse(res, append ? this.mediaItems.length : 0);
      this.mediaItems = append ? [...this.mediaItems, ...nextItems] : nextItems;
      this.activeInvitationId = res?.data?.invitation?.id || this.activeInvitationId;
      this.updatePagination(res);
      this.isMediaLoading = false;
      this.isMediaLoadingMore = false;
    }).catch(error => {
      console.error('Failed to load invitation media', error);
      if (!append) {
        this.mediaItems = [];
      }
      this.isMediaLoading = false;
      this.isMediaLoadingMore = false;
    });
  }

  private mapFilteredInvitationsResponse(res: any, startIndex = 0): InvitationMediaItem[] {
    const responseItems = this.extractResponseItems(res);

    return responseItems.map((item: any, index: number) => {
      const fileName = item.name || item.file_name || item.filename || item.title || `${this.activeMediaType}-${index + 1}`;
      return {
        id: item.id,
        name: this.getMediaName(fileName),
        fullPath: item.full_path || item.fullPath || item.url || item.path,
        posterPath: item.poster || item.poster_path || item.thumbnail || item.thumbnail_path,
        type: this.activeMediaType,
        gender: this.activeGender,
        age: this.activeAge,
        position: item.position || startIndex + index + 1
      };
    });
  }

  private extractResponseItems(res: any): any[] {
    if (Array.isArray(res?.data?.files?.data)) {
      return res.data.files.data;
    }
    if (Array.isArray(res?.data?.data)) {
      return res.data.data;
    }
    if (Array.isArray(res?.data)) {
      return res.data;
    }
    if (Array.isArray(res?.items)) {
      return res.items;
    }
    if (Array.isArray(res?.images) && this.activeMediaType === 'image') {
      return res.images;
    }
    if (Array.isArray(res?.videos) && this.activeMediaType === 'video') {
      return res.videos;
    }
    if (Array.isArray(res)) {
      return res;
    }

    return [];
  }

  private updatePagination(res: any): void {
    const pagination = res?.data?.files || res?.data || res;
    this.currentPage = pagination?.current_page || this.currentPage;
    this.lastPage = pagination?.last_page || this.currentPage;
  }

  private getMediaName(fileName: string): string {
    return fileName.replace(/\.[^/.]+$/, '');
  }

  private getApiMediaType(): string {
    return this.activeMediaType === 'image' ? 'images' : 'videos';
  }

}
